import { Box, Typography } from "@mui/material";
import { getDisplayImage } from "@/utils/helper";
import AccessibilityChip, { AccessibilityChipProps } from "@/components/filterDrawer/AccessibilityChip";
import { Place } from "@/types/common";
import BaseCard from "@/shared/components/BaseCard";
import { FONT_SIZES } from "@/shared/constants/typography";
import PlaceThumbnail from "@/shared/components/PlaceThumbnail";

interface RecentlyVisitedCardProps {
    place: Place;
    visitedAt?: string;
}

const getBestAccessibility = (
    contributions: RecentlyVisitedCardProps["place"]["contributions"]
): string => {
    const best = contributions?.find((c) => c.is_best) ?? contributions?.[0];
    return best?.accessibility || "no_data";
};

// ─── Image helper ─────────────────────────────────────────────────────────────
const getPlaceImage = (place: Place): string => {
    const contributions = place?.contributions ?? [];
    const bestContribution =
        contributions.find((c) => c.is_best) ?? contributions[0];

    return getDisplayImage(bestContribution?.media);
};

// ─── Component ────────────────────────────────────────────────────────────────
const RecentlyVisitedCard = ({ place }: RecentlyVisitedCardProps) => {
    const imageUrl = getPlaceImage(place);
    const accessibility = getBestAccessibility(place.contributions) as AccessibilityChipProps["status"];

    return (
        <BaseCard
            sx={{
                display: "flex",
                alignItems: "center",
                mt: "3.5%",
                gap: "3%",
            }}
        >
            {/* Thumbnail */}
            <PlaceThumbnail 
                src={imageUrl} 
                alt={place.name}
                size={{ xs: "14vw", sm: 56 }}
                borderRadius="12px"
            />

            {/* Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: FONT_SIZES.BODY,
                        fontWeight: 700,
                        color: "#000000",
                        mb: "4px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {place.name}
                </Typography>

                <Typography
                    sx={{
                        fontSize: FONT_SIZES.TINY,
                        color: "#626262",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mb: "6px",
                    }}
                >
                    {place.address_text}
                </Typography>

                <AccessibilityChip status={accessibility || "no_data"} />
            </Box>
        </BaseCard>
    );
};

export default RecentlyVisitedCard;
